import moment from 'moment'
import { ColumnsType } from 'antd/es/table'
import { SortOrder } from 'antd/es/table/interface'
import AmendmentCell from 'features/ContractDetails/Facilities/HSTable/AmendmentCell/AmendmentCell'
import { CellWithSubtitle } from '../../../../components/CellWithSubtitle/CellWithSubtitle'
import { Sorter } from 'components/Sorter/Sorter'
import { Link } from '../../../../components/Link'
import { Tooltip } from '../../../../components/Tooltip/Tooltip'
import { AddLOPCell } from './AddLOPCell/AddLOPCell'
import {
  timelineDateFormat,
  timelineMonthYearFormat
} from '../../../../components/Timelines/constants'
import { ReactComponent as WarningRed16 } from 'assets/svg/WarningRed16.svg'
import { IContractHealthSystem } from '../types'
import { SIGNATORY_STATUS_LABELS } from '../../constants'
import { UserListItem } from 'features/ContractDetails/ContractDetailsGeneral/ContractDetailsDocuments/ContractSignatoryUsers/UserListItemSignatory/UserListItemSignatory'
import { ReactComponent as Information16 } from 'assets/svg/Information.svg'

const _getMyCommunityLink = (
  name: string,
  { uuid, hospital_level }: IContractHealthSystem
) =>
  hospital_level ? (
    <Tooltip className="name-column" title={name}>
      {name}
    </Tooltip>
  ) : (
    <CellWithSubtitle
      title={
        <Tooltip title={name}>
          <Link
            className="name-column"
            to={`/my-community/health-system/${uuid}/`}
          >
            {name}
          </Link>
        </Tooltip>
      }
    />
  )

export const columnsShortView = (
  canSeeSignatories: boolean,
  canSeeHealthSystem: boolean
) => {
  let columns = [
    {
      dataIndex: 'not_completed',
      key: 'not_completed',
      width: 40,
      title: '',
      render: (_, { not_completed, hospital_level }) =>
        not_completed &&
        !hospital_level && (
          <Tooltip title="Provide participation details." className="warning">
            <WarningRed16 />
          </Tooltip>
        )
    },
    {
      dataIndex: 'name',
      key: 'name',
      width: 480,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<IContractHealthSystem>
          dataIndex="name"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          {canSeeHealthSystem ? 'Health system' : 'Hospital'}
        </Sorter>
      ),
      render: _getMyCommunityLink,
      sorter: true,
      showSorterTooltip: false
    },
    {
      dataIndex: 'pricing_effective_date',
      key: 'pricing_effective_date',
      width: 280,
      title: 'Pricing effective date',
      render: (
        dateString,
        { hospital_level, hs_amendment_document, amendment_document }
      ) => {
        if (hospital_level && hs_amendment_document && !amendment_document)
          return '-'
        return !!dateString
          ? moment(dateString).format(timelineDateFormat)
          : '-'
      }
    },
    {
      dataIndex: 'initial_calculation_date',
      key: 'initial_calculation_date',
      width: 280,
      title: () => (
        <Tooltip
          className="row gap-8"
          title="The calculation will be started on the first day of selected month"
        >
          Initial calculation date
          <Information16 />
        </Tooltip>
      ),
      render: (
        dateString,
        { hospital_level, hs_amendment_document, amendment_document }
      ) => {
        if (hospital_level && hs_amendment_document && !amendment_document)
          return '-'
        return !!dateString
          ? moment(dateString).format(timelineMonthYearFormat)
          : '-'
      }
    },
    {
      dataIndex: 'amendment_document',
      key: 'amendment_document',
      width: 380,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<IContractHealthSystem>
          dataIndex="amendment_document"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          LoP / Amendment
        </Sorter>
      ),
      render: (
        _,
        { amendment_document, hospital_level, hs_amendment_document }
      ) => {
        if (hospital_level && hs_amendment_document && !amendment_document) {
          return <>Participates via HS LOP</>
        }
        return amendment_document ? (
          <AmendmentCell amendment_document={amendment_document} />
        ) : (
          '-'
        )
      },
      sorter: true,
      showSorterTooltip: false
    }
  ] as ColumnsType<IContractHealthSystem>

  if (canSeeSignatories) {
    columns = columns.concat([
      {
        dataIndex: 'community_signatory',
        key: 'community_signatory',
        width: 380,
        title: 'Health system signatory',
        render: (
          _,
          {
            community_signatory,
            hospital_level,
            hs_amendment_document,
            amendment_document
          }
        ) => {
          if (hospital_level && hs_amendment_document && !amendment_document)
            return '-'

          return community_signatory ? (
            <UserListItem
              bigAvatar
              user={community_signatory?.signatory ?? undefined}
              email={community_signatory?.email ?? undefined}
              statusLabel={
                community_signatory?.status
                  ? SIGNATORY_STATUS_LABELS[community_signatory?.status]
                  : undefined
              }
            />
          ) : (
            '-'
          )
        }
      },
      {
        dataIndex: 'vendor_signatory',
        key: 'vendor_signatory',
        width: 380,
        title: 'Vendor signatory',
        render: (
          _,
          {
            vendor_signatory,
            hospital_level,
            hs_amendment_document,
            amendment_document
          }
        ) => {
          if (hospital_level && hs_amendment_document && !amendment_document)
            return '-'

          return vendor_signatory ? (
            <UserListItem
              bigAvatar
              user={vendor_signatory?.signatory ?? undefined}
              email={vendor_signatory?.email ?? undefined}
              statusLabel={
                vendor_signatory?.status
                  ? SIGNATORY_STATUS_LABELS[vendor_signatory?.status]
                  : undefined
              }
            />
          ) : (
            '-'
          )
        }
      }
    ])
  }

  columns.push({
    dataIndex: 'action',
    key: 'action',
    width: 64,
    render: (_, selectedHSRow) => <AddLOPCell selectedHSRow={selectedHSRow} />
  })

  return columns
}

export const onboardedHsColumns = (
  canSeeSignatories: boolean,
  canSeeHealthSystem: boolean
) => {
  let columns = [
    {
      dataIndex: 'name',
      key: 'name',
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<IContractHealthSystem>
          dataIndex="name"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          {canSeeHealthSystem ? 'Health system' : 'Hospital'}
        </Sorter>
      ),
      render: _getMyCommunityLink,
      sorter: true,
      showSorterTooltip: false
    }
  ] as ColumnsType<IContractHealthSystem>

  if (canSeeSignatories) {
    columns = columns.concat([
      {
        dataIndex: 'community_signatory',
        key: 'community_signatory',
        width: 380,
        title: 'Health system signatory',
        render: (_, { community_signatory }) =>
          community_signatory ? (
            <UserListItem
              bigAvatar
              user={community_signatory?.signatory ?? undefined}
              email={community_signatory?.email ?? undefined}
              statusLabel={
                SIGNATORY_STATUS_LABELS[
                  community_signatory?.status ?? 'waiting_for_signature'
                ]
              }
            />
          ) : (
            '-'
          )
      },
      {
        dataIndex: 'vendor_signatory',
        key: 'vendor_signatory',
        width: 380,
        title: 'Vendor signatory',
        render: (_, { vendor_signatory }) =>
          vendor_signatory ? (
            <UserListItem
              bigAvatar
              user={vendor_signatory?.signatory ?? undefined}
              email={vendor_signatory?.email ?? undefined}
              statusLabel={
                SIGNATORY_STATUS_LABELS[
                  vendor_signatory?.status ?? 'waiting_for_signature'
                ]
              }
            />
          ) : (
            '-'
          )
      }
    ])
  }

  return columns
}
